import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  DestroyRef,
  OnInit,
  computed,
  inject,
  signal,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { ActivatedRoute, Router, RouterLink } from '@angular/router';
import { Subject, forkJoin, of } from 'rxjs';
import { switchMap } from 'rxjs/operators';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { ToastModule } from 'primeng/toast';
import { DialogModule } from 'primeng/dialog';
import { MessageService } from 'primeng/api';
import { BackButton } from '@components/ui/back-button/back-button';
import { PrimaryButton } from '@components/button/primary-button/primary-button';
import { SecondaryButton } from '@components/button/secondary-button/secondary-button';
import { Loading } from '@components/ui/loading/loading';
import { MsfInput } from '@components/ui/forms/input/input';
import { MsfSelect } from '@components/select/select';
import { MsfMultiSelect } from '@components/multi-select/multi-select';
import { CheckboxComponent } from '@components/checkbox/checkbox.component';
import { CopyableIdentifierComponent } from '@app/pages/admin/components/copyable-identifier/copyable-identifier';
import { NotificationRecipientCatalog } from './notification-recipient-catalog';
import { NotificationRecipientPickerComponent } from './notification-recipient-picker';
import { OrchestrationTableHeadingComponent } from '@app/pages/admin/components/orchestration-table-heading/orchestration-table-heading';
import {
  DataTableComponent,
  TableColumn,
  TableConfig,
} from '@components/data-table/data-table.component';
import { AssignmentProcessService as AssignmentProcessApiService } from '@6grain/masofa-monolith-api-client/common/api/assignmentProcess.service';
import {
  NotificationEscalationStepDto,
  NotificationRecipientSelectorDto,
  NotifiableEventDto,
  NotificationRuleDto,
  NotificationDeliveryDto,
  UserNotificationsApiService,
} from '@app/services/user-notifications-api.service';
import {
  toRecipientSelectorDraft,
  toRecipientSelectorSave,
} from '@app/pages/admin/notification-recipient-selector.util';
import {
  NOTIFICATION_RULES_LIST_PATH,
  notificationRuleUrl,
} from '@app/pages/admin/admin-notifications-routes.util';
import { orchestrationEventUrl, orchestrationProcessUrl } from '@app/pages/admin/orchestration-identity.util';
import { domainEventSourceProcess } from '@app/pages/admin/orchestration-process-events.util';
import {
  notificationJournalColumns,
  notificationJournalTableConfig,
} from '@app/pages/admin/notification-journal-table.util';
import { NotificationTemplateFieldsComponent } from '@app/pages/admin/components/notification-template-fields/notification-template-fields';
import { notificationBaseTemplateName, notificationTemplateFieldEvents } from '@app/pages/admin/notification-template-fields.util';

interface RecipientDraft {
  kind: string;
  entityId: string;
  roleName: string;
  emailAddress: string;
}

interface EscalationDraft {
  afterMinutes: number;
  channelKeys: string[];
  recipients: RecipientDraft[];
}

@Component({
  selector: 'app-admin-notification-rule-page',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    RouterLink,
    TranslateModule,
    ToastModule,
    DialogModule,
    BackButton,
    PrimaryButton,
    SecondaryButton,
    Loading,
    MsfInput,
    MsfSelect,
    MsfMultiSelect,
    CheckboxComponent,
    CopyableIdentifierComponent,
    OrchestrationTableHeadingComponent,
    DataTableComponent,
    NotificationTemplateFieldsComponent,
    NotificationRecipientPickerComponent,
  ],
  templateUrl: './admin-notification-rule-page.html',
  styleUrl: './admin-notification-rule-page.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [MessageService, NotificationRecipientCatalog],
})
export class AdminNotificationRulePageComponent implements OnInit {
  private readonly api = inject(UserNotificationsApiService);
  private readonly processesApi = inject(AssignmentProcessApiService);
  private readonly messages = inject(MessageService);
  private readonly destroyRef = inject(DestroyRef);
  private readonly route = inject(ActivatedRoute);
  private readonly router = inject(Router);
  private readonly cdr = inject(ChangeDetectorRef);
  private readonly translate = inject(TranslateService);
  private readonly reload$ = new Subject<void>();

  readonly listPath = NOTIFICATION_RULES_LIST_PATH;
  readonly loading = signal(true);
  readonly editing = signal(false);
  readonly testing = signal(false);
  readonly savedRule = signal<NotificationRuleDto | null>(null);
  readonly summary = computed(() => {
    const row = this.savedRule();
    if (!row) return null;
    return {
      ...row,
      eventLabel: this.events().find(item => item.value === row.eventKey)?.label || row.eventKey,
      eventUrl: orchestrationEventUrl(row.eventKey),
      hideSourceProcess: domainEventSourceProcess(row.eventKey) !== null,
      processLabel: this.processes().find(item => item.value === row.sourceProcessDefinitionKey)?.label
        || row.sourceProcessDefinitionKey || 'adminNotifications.anyProcess',
      processUrl: orchestrationProcessUrl(row.sourceProcessDefinitionKey),
      hideTemplate: notificationBaseTemplateName(row.eventKey, row.sourceProcessDefinitionKey ?? '') !== null,
      templateLabel: this.templates().find(item => item.id === row.templateId)?.name || row.templateId,
      channelLabels: row.channels.map(key => this.channelOptions().find(item => item.value === key)?.label || key).join(', '),
      recipientLabels: row.recipients.map(item => ({
        kind: this.recipientKinds.find(kind => kind.value === item.kind)?.label || item.kind,
        value: item.emailAddress || item.roleName || item.entityId || '—',
      })),
      escalationLabels: row.escalation.map(step => ({
        afterMinutes: step.afterMinutes,
        channels: step.channelKeys.map(key => this.channelOptions().find(item => item.value === key)?.label || key).join(', '),
        recipients: step.recipients.map(item => item.emailAddress || item.roleName || item.entityId || '—').join(', '),
      })),
    };
  });
  readonly missing = signal(false);
  readonly ruleId = signal<string | null>(null);
  readonly isCreate = computed(() => !this.ruleId());

  readonly events = signal<{ label: string; value: string }[]>([]);
  readonly notifiableEvents = signal<NotifiableEventDto[]>([]);
  readonly selectedRuleEventKey = signal('');
  readonly templateFieldEvents = computed(() =>
    notificationTemplateFieldEvents(this.notifiableEvents(), [this.selectedRuleEventKey()]),
  );
  readonly processes = signal<{ label: string; value: string }[]>([]);
  readonly templates = signal<{ id: string; name: string; isRetired: boolean }[]>([]);
  readonly channelOptions = signal<{ label: string; value: string }[]>([]);
  readonly deliveries = signal<NotificationDeliveryDto[]>([]);
  readonly journalDetail = signal<NotificationDeliveryDto | null>(null);
  readonly journalView = computed(() => {
    const row = this.journalDetail();
    if (!row) return null;
    const link = row.deepLink?.trim() ?? '';
    return { ...row, deepLinkUrl: link.startsWith('/') ? link : null };
  });
  readonly deliveryColumns = computed<TableColumn[]>(() => notificationJournalColumns());
  readonly deliveryTableConfig = computed<TableConfig>(() =>
    notificationJournalTableConfig('adminNotifications.noDeliveries'),
  );

  readonly processOptions = computed(() => [
    { label: 'adminNotifications.anyProcess', value: '' },
    ...this.processes(),
  ]);
  readonly selectedSourceProcessKey = signal('');
  readonly lockedTemplate = computed(() => {
    const process = this.lockedSourceProcess()?.processKey ?? this.selectedSourceProcessKey();
    const name = notificationBaseTemplateName(this.selectedRuleEventKey(), process);
    if (!name) {
      return null;
    }
    const row = this.templates().find(item => item.name === name);
    return { name, id: row?.id ?? '' };
  });
  readonly lockedSourceProcess = computed(() => {
    const owner = domainEventSourceProcess(this.selectedRuleEventKey());
    if (!owner) {
      return null;
    }
    return { processKey: owner.processKey, persist: owner.persist };
  });
  readonly templateOptions = computed(() => {
    const current = this.ruleTemplateId;
    return this.templates()
      .filter((row) => !row.isRetired || row.id === current)
      .map((row) => ({ label: row.name, value: row.id }));
  });

  readonly priorities = [
    { label: 'Low', value: 'Low' },
    { label: 'Normal', value: 'Normal' },
    { label: 'High', value: 'High' },
    { label: 'Critical', value: 'Critical' },
  ];
  readonly recipientKinds = [
    { label: 'adminNotifications.kindUser', value: 'User' },
    { label: 'adminNotifications.kindRole', value: 'Role' },
    { label: 'adminNotifications.kindOrganization', value: 'Organization' },
    { label: 'adminNotifications.kindRegion', value: 'Region' },
    { label: 'adminNotifications.kindFarm', value: 'Farm' },
    { label: 'adminNotifications.kindEmail', value: 'Email' },
  ];

  ruleName = '';
  ruleEventKey = '';
  ruleSourceProcessKey = '';
  rulePriority = 'Normal';
  ruleTemplateId = '';
  ruleActive = true;
  ruleChannels: string[] = [];
  ruleRecipients: RecipientDraft[] = [];
  ruleEscalation: EscalationDraft[] = [];
  testUserId = '';
  private original: NotificationRuleDto | null = null;
  private queryApplied = false;

  ngOnInit(): void {
    this.reload$
      .pipe(
        switchMap(() => {
          this.loading.set(true);
          this.missing.set(false);
          const id = this.ruleId();
          return forkJoin({
            rules: this.api.getRules(),
            templates: this.api.getTemplates(),
            settings: this.api.getSettings(),
            events: this.api.getEvents(),
            processes: this.processesApi.assignmentsProcessesGet(),
            deliveries: id ? this.api.getJournal({ ruleId: id }) : of([] as NotificationDeliveryDto[]),
          });
        }),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe({
        next: (bundle) => {
          this.notifiableEvents.set(bundle.events);
          this.events.set(bundle.events.map((item) => ({ label: item.displayName, value: item.key })));
          this.processes.set(
            (bundle.processes ?? []).map((item: { key?: string; displayName?: string }) => ({
              label: item.displayName || item.key || '',
              value: item.key || '',
            })),
          );
          this.templates.set(bundle.templates.map((row) => ({
            id: row.id,
            name: row.name,
            isRetired: row.isRetired,
          })));
          this.channelOptions.set(
            (bundle.settings.channels ?? [])
              .filter((channel) => channel.enabled)
              .map((channel) => ({ label: channel.displayName, value: channel.key })),
          );
          const id = this.ruleId();
          if (id) {
            const row = bundle.rules.find((item) => item.id === id) ?? null;
            this.original = row;
            if (!row) {
              this.deliveries.set([]);
              this.missing.set(true);
              this.loading.set(false);
              this.cdr.markForCheck();
              return;
            }
            this.applyRule(row);
            this.deliveries.set(bundle.deliveries);
          } else {
            this.resetCreateForm();
            this.applyCreateQuery();
            this.deliveries.set([]);
          }
          this.loading.set(false);
          this.cdr.markForCheck();
        },
        error: () => {
          this.loading.set(false);
          this.showError('adminNotifications.loadFailed');
          this.cdr.markForCheck();
        },
      });
    this.route.paramMap
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((params) => {
        const id = params.get('ruleId')?.trim() || null;
        this.ruleId.set(id);
        this.queryApplied = false;
        this.reload();
      });
  }

  beginEdit(): void {
    if (!this.original) return;
    this.applyRule(this.original);
    this.editing.set(true);
  }

  closeEdit(): void {
    this.editing.set(false);
    if (this.original) this.applyRule(this.original);
  }

  openTest(): void {
    this.testUserId = '';
    if ((this.original?.recipients.length ?? 0) > 0) {
      this.testRule();
      return;
    }
    this.testing.set(true);
  }

  reload(): void {
    this.closeEdit();
    this.testing.set(false);
    this.reload$.next();
  }

  addRecipient(): void {
    this.ruleRecipients = [...this.ruleRecipients, this.emptyRecipient()];
  }

  removeRecipient(index: number): void {
    this.ruleRecipients = this.ruleRecipients.filter((_, i) => i !== index);
  }

  addEscalationStep(): void {
    this.ruleEscalation = [
      ...this.ruleEscalation,
      { afterMinutes: 5, channelKeys: [], recipients: [this.emptyRecipient()] },
    ];
  }

  removeEscalationStep(index: number): void {
    this.ruleEscalation = this.ruleEscalation.filter((_, i) => i !== index);
  }

  saveRule(): void {
    const recipients = this.toSelectors(this.ruleRecipients);
    const channels = this.ruleChannels.filter((key) => !!key);
    if (!this.ruleEventKey || !(this.lockedTemplate()?.id || this.ruleTemplateId) || recipients.length === 0 || channels.length === 0) {
      this.showError('adminNotifications.saveFailed');
      return;
    }
    const body: Omit<NotificationRuleDto, 'id'> = {
      name: this.ruleName.trim(),
      eventKey: this.ruleEventKey,
      sourceProcessDefinitionKey: this.sourceProcessToSave(),
      priority: this.rulePriority,
      templateId: this.lockedTemplate()?.id || this.ruleTemplateId,
      isActive: this.ruleActive,
      recipients,
      channels,
      escalation: this.toEscalation(this.ruleEscalation),
    };
    const id = this.ruleId();
    const request = id ? this.api.updateRule(id, body) : this.api.createRule(body);
    request.pipe(takeUntilDestroyed(this.destroyRef)).subscribe({
      next: (saved) => {
        if (!id && saved.id) {
          void this.router.navigateByUrl(notificationRuleUrl(saved.id), { replaceUrl: true });
          return;
        }
        this.original = { ...saved, id: id ?? saved.id };
        this.applyRule(this.original);
        this.editing.set(false);
        this.cdr.markForCheck();
      },
      error: () => this.showError('adminNotifications.saveFailed'),
    });
  }

  setActive(isActive: boolean): void {
    const row = this.original;
    if (!row) {
      return;
    }
    this.api
      .updateRule(row.id, { ...row, isActive })
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (saved) => {
          this.original = saved;
          this.applyRule(saved);
          this.cdr.markForCheck();
        },
        error: () => this.showError('adminNotifications.saveFailed'),
      });
  }

  deleteRule(): void {
    const id = this.ruleId();
    if (!id) {
      return;
    }
    this.api
      .deleteRule(id)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: () => void this.router.navigateByUrl(NOTIFICATION_RULES_LIST_PATH),
        error: () => this.showError('adminNotifications.saveFailed'),
      });
  }

  testRule(): void {
    const id = this.ruleId();
    if (!id) {
      return;
    }
    const override = this.testUserId.trim();
    if (!override && (this.original?.recipients.length ?? 0) === 0) {
      this.showError('adminNotifications.testUserRequired');
      return;
    }
    this.api
      .testRule(id, override || undefined)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: () => this.reload(),
        error: () => this.showError('adminNotifications.testFailed'),
      });
  }

  private showError(key: string): void {
    this.messages.add({ severity: 'error', summary: this.translate.instant(key) });
  }

  onRuleEventChange(eventKey: string): void {
    this.selectedRuleEventKey.set(eventKey);
    this.applyLockedSource(eventKey);
    this.applyLockedTemplate();
  }

  onSourceProcessChange(processKey: string): void {
    this.selectedSourceProcessKey.set(processKey);
    this.applyLockedTemplate();
  }

  private sourceProcessToSave(): string | null {
    const locked = this.lockedSourceProcess();
    if (locked) {
      return locked.persist ? locked.processKey : null;
    }
    return this.ruleSourceProcessKey || null;
  }

  private applyLockedSource(eventKey: string): void {
    const owner = domainEventSourceProcess(eventKey);
    if (!owner) {
      return;
    }
    this.ruleSourceProcessKey = owner.persist ? owner.processKey : '';
  }

  private applyLockedTemplate(): void {
    const owner = domainEventSourceProcess(this.ruleEventKey);
    this.selectedSourceProcessKey.set(owner?.processKey ?? this.ruleSourceProcessKey);
    const locked = this.lockedTemplate();
    if (locked?.id) {
      this.ruleTemplateId = locked.id;
    }
  }

  closeCreate(): void {
    void this.router.navigateByUrl(NOTIFICATION_RULES_LIST_PATH, { replaceUrl: true });
  }

  openJournalRow(row: NotificationDeliveryDto): void {
    this.journalDetail.set(row);
  }

  onJournalDetailVisibleChange(visible: boolean): void {
    if (!visible) {
      this.journalDetail.set(null);
    }
  }

  private applyRule(row: NotificationRuleDto): void {
    this.savedRule.set(row);
    this.ruleName = row.name;
    this.ruleEventKey = row.eventKey;
    this.selectedRuleEventKey.set(row.eventKey);
    this.ruleSourceProcessKey = row.sourceProcessDefinitionKey ?? '';
    this.applyLockedSource(row.eventKey);
    this.applyLockedTemplate();
    this.rulePriority = row.priority;
    this.ruleTemplateId = row.templateId;
    this.ruleActive = row.isActive;
    this.ruleChannels = [...row.channels];
    this.ruleRecipients = row.recipients.length
      ? row.recipients.map((item) => toRecipientSelectorDraft(item))
      : [this.emptyRecipient()];
    this.ruleEscalation = row.escalation.map((step) => ({
      afterMinutes: step.afterMinutes,
      channelKeys: [...step.channelKeys],
      recipients: step.recipients.map((item) => toRecipientSelectorDraft(item)),
    }));
    if (this.ruleSourceProcessKey && !this.processes().some((item) => item.value === this.ruleSourceProcessKey)) {
      this.processes.set([...this.processes(), {
        label: this.ruleSourceProcessKey,
        value: this.ruleSourceProcessKey,
      }]);
    }
  }

  private resetCreateForm(): void {
    this.ruleName = '';
    this.ruleEventKey = this.events()[0]?.value ?? '';
    this.selectedRuleEventKey.set(this.ruleEventKey);
    this.ruleSourceProcessKey = '';
    this.rulePriority = 'Normal';
    this.ruleTemplateId = this.templateOptions()[0]?.value ?? '';
    this.ruleActive = true;
    this.ruleChannels = this.channelOptions()[0] ? [this.channelOptions()[0].value] : [];
    this.ruleRecipients = [this.emptyRecipient()];
    this.ruleEscalation = [];
  }

  private applyCreateQuery(): void {
    if (this.queryApplied) {
      return;
    }
    const params = this.route.snapshot.queryParamMap;
    const eventKey = params.get('eventKey')?.trim();
    if (eventKey) {
      this.ruleEventKey = eventKey;
      this.selectedRuleEventKey.set(eventKey);
    }
    const source = params.get('sourceProcessDefinitionKey')?.trim() ?? '';
    this.ruleSourceProcessKey = source;
    this.applyLockedSource(this.ruleEventKey);
    this.applyLockedTemplate();
    if (source && !this.processes().some((item) => item.value === source)) {
      this.processes.set([...this.processes(), { label: source, value: source }]);
    }
    const priority = params.get('priority')?.trim();
    if (priority) {
      this.rulePriority = priority;
    }
    this.queryApplied = true;
    if (params.get('create') === '1') {
      void this.router.navigate([], {
        relativeTo: this.route,
        queryParams: { create: null },
        queryParamsHandling: 'merge',
        replaceUrl: true,
      });
    }
  }

  private emptyRecipient(): RecipientDraft {
    return { kind: 'User', entityId: '', roleName: '', emailAddress: '' };
  }

  private toSelectors(rows: RecipientDraft[]): NotificationRecipientSelectorDto[] {
    const result: NotificationRecipientSelectorDto[] = [];
    for (const row of rows) {
      const selector = toRecipientSelectorSave(row);
      if (selector) {
        result.push({
          kind: selector.kind,
          entityId: selector.entityId,
          roleName: selector.roleName,
          emailAddress: selector.emailAddress,
        });
      }
    }
    return result;
  }

  private toEscalation(rows: EscalationDraft[]): NotificationEscalationStepDto[] {
    return rows
      .filter((step) => step.afterMinutes > 0)
      .map((step) => ({
        afterMinutes: step.afterMinutes,
        channelKeys: step.channelKeys.filter((key) => !!key),
        recipients: this.toSelectors(step.recipients),
      }))
      .filter((step) => step.channelKeys.length > 0 || step.recipients.length > 0);
  }
}
